// src/main.jsx
import React from "react";
import ReactDOM from "react-dom/client";
import App from "./App";
// Inter is bundled (not fetched from Google) so the app renders in its own
// face on dead ship/PPU wifi — the woff2 files ride the service-worker
// precache like the rest of the shell.
import "@fontsource/inter/400.css";
import "@fontsource/inter/500.css";
import "@fontsource/inter/600.css";
import "@fontsource/inter/700.css";
import "./index.css";
import "./supabaseClient";
import { restoreDraftVault, installDraftVault } from "./lib/draftVault";
import { sweepSubmittedExpenseDrafts, shedFinishedBackupCopies } from "./lib/localDraftSpace";
import { watchStoragePersistence } from "./lib/persistentStorage";

// A deploy mid-session replaces the hashed JS chunks an open page still
// references, so the next lazy-loaded screen throws (Paul's "TypeError:
// FetchEvent" 2026-07-02). One reload picks up the new build; guard with a
// timestamp so a genuinely broken network can't reload-loop.
window.addEventListener("vite:preloadError", (event) => {
  const last = Number(sessionStorage.getItem("chunkReloadAt") || 0);
  if (Date.now() - last > 30000) {
    event.preventDefault();
    sessionStorage.setItem("chunkReloadAt", String(Date.now()));
    window.location.reload();
  }
});

// Ask the browser to keep our local storage (login session + slip drafts) from
// being evicted. Granted automatically for installed/home-screen PWAs and
// engaged sites; a no-op where unsupported. Helps "log in once" + drafts survive.
// The browser answers SILENTLY, and it can say no: Jill's iPad lost the whole
// origin (app shell + drafts + vault) to an iOS eviction on 2026-08-07 with
// this request in place since June. watchStoragePersistence (called after
// render below) reports each device's actual answer to seapa_debug_log and
// retries a denial from inside a real tap.
if (navigator.storage?.persist) {
  navigator.storage.persisted?.().then((already) => {
    if (!already) navigator.storage.persist().catch(() => {});
  }).catch(() => {});
}

function Root() {
  return <App />;
}

// installDraftVault() patches localStorage mirroring and re-sweeps; it must run
// before render so pages read a healed store. restoreDraftVault() copies drafts
// back from the IndexedDB mirror when iOS wiped localStorage.
function boot() {
  installDraftVault();
  // Submitted paperwork is finished paperwork: it belongs in the account, not
  // on the phone. Runs AFTER installDraftVault so the deletes mirror into the
  // IndexedDB vault too, otherwise the space is never actually reclaimed and
  // restoreDraftVault() copies everything straight back (John Herring's iPhone,
  // 2026-08-06 — a season of submitted expense reports filled Safari's quota
  // and crashed the editor on signing). Cheap synchronous pass over
  // localStorage keys; wrapped so it can never block a boot.
  try { sweepSubmittedExpenseDrafts(); } catch { /* never block startup */ }
  // ...and the `_prev` backup copies of finished paperwork, which nothing in
  // the app has ever read. On submitted SLIPS this is the biggest safe win
  // available: a filed slip envelope averages 195 KB against 23 KB for a
  // draft, because it carries the signature images. The live copy of a filed
  // slip is deliberately left alone (Jill's 2026-07-25 ruling after the
  // VVE26-VOY007 incident); only the unread backup goes.
  try { shedFinishedBackupCopies(); } catch { /* never block startup */ }
  ReactDOM.createRoot(document.getElementById("root")).render(
    <Root />
  );
  // After render, off the boot path: report whether the browser actually
  // honors our persistent-storage request (see the persist() call above).
  // storageIntact was read BEFORE installDraftVault re-stamped the sentinel,
  // so it faithfully says whether local data survived since the last run.
  setTimeout(() => {
    watchStoragePersistence(storageIntact).catch(() => { /* best-effort */ });
  }, 3000);
}

// Fast path (Jill approved 2026-07-12, ~1s faster on every cold open): the
// "seapa_vault_ok" sentinel means localStorage survived, so the restore would
// only re-copy keys that are already present — a no-op. Skip the pre-paint
// IndexedDB read and render immediately, then run the restore in the background
// anyway as a belt-and-suspenders for any stray missing key. Only when the
// sentinel is GONE (storage wiped — the exact case the vault exists for) do we
// block first paint on the restore.
let storageIntact = false;
try { storageIntact = localStorage.getItem("seapa_vault_ok") === "1"; } catch { /* blocked → take the safe gated path */ }
if (storageIntact) {
  boot();
  restoreDraftVault().catch(() => { /* background heal — best effort */ });
} else {
  restoreDraftVault()
    .catch(() => { /* render regardless */ })
    .then(boot);
}